import { DOCS_URLS } from "@p0security/frontend-core";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { TailscaleComponents } from "@p0security/integrations-tailscale-shared/components";
import {
  VAULT_OUTSIDE_CONNECTOR_CLOUD,
  renderHosting,
} from "@p0security/integrations-tailscale-shared/hosting";
import type {
  TailscaleConnectorConfig,
  TailscaleIntegration,
  TailscaleTailnetConfig,
} from "@p0security/integrations-tailscale-shared/types";
import { p0InvokerEmail } from "@p0security/integrations-vaulted-credential-shared";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import {
  Alert,
  Collapse,
  CommandDisplay,
  Flex,
  Spacing,
  Typography,
} from "@p0security/ui";
import { assertNever } from "@p0security/util/assertNever";
import { compact, sortBy } from "lodash-es";
import type { ReactNode } from "react";

import { renderConnectorTerraform } from "./connector-iac.ts";
import {
  ConnectorHelp,
  type CredentialTerraform,
  ScopeList,
  TailnetSteps,
} from "./steps.tsx";
import {
  type ReadGrantBlock,
  isVaultAccountBlock,
  renderFederatedIdentityTerraform,
  renderReadGrantTerraform,
} from "./tailnet-iac.ts";

// This copies Auth0's installer (app#7529 @ 3c8d887b9a) instead of sharing
// it, so adding Tailscale changes no other integration.

const { Link, Paragraph } = Typography;

type TailscaleInstaller = FrontendInstaller<typeof TailscaleComponents>;
type InstallContext = FrontendInstallContext<TailscaleIntegration>;

/** Creates the provider that lists P0's installs of `integration`'s
 * iam-write component: the accounts, or projects, that a field placing a
 * connector or a secret can name. */
const newIamWriteInstallsProvider = (integration: string) =>
  otherInstalledIntegration({ component: "iam-write", integration });

/** The connectors a tailnet can name: this integration's own installed
 * connectors, each with where it runs.
 *
 * This reads the installer's live config instead of fetching: the pointer
 * targets a sibling component, not another integration, so
 * `otherInstalledIntegration` would spend a read on a document the install
 * context already holds.
 */
const listConnectors = async ({ config }: InstallContext) =>
  sortBy(
    compact(
      Object.entries(config ? installedItems("connector", config) : {}).map(
        ([id, connector]) =>
          connector && {
            id,
            label: `${id}: ${renderHosting(connector.hosting)}`,
          }
      )
    ),
    "label"
  );

/** Whether a stored item has been set up: its page then shows the form, and
 * its Terraform only on request. */
const isSetUp = (item: { state?: string } | undefined) =>
  item?.state === "configure" || item?.state === "installed";

const DocsLink: React.FC = () => (
  <Link
    href={DOCS_URLS.TAILSCALE_INSTALL}
    rel="noopener noreferrer"
    target="_blank"
  >
    Tailscale install guide
  </Link>
);

/** Why a connector on Cloud Run can't be set up yet: its Terraform lets in
 * the service account P0 invokes it as, which is the one P0's Google Cloud
 * integration has. */
const MISSING_GOOGLE_CLOUD =
  "P0 invokes a connector on Cloud Run as the service account of its Google Cloud integration, which isn't installed. Install P0's Google Cloud IAM management for the connector's project first.";

const MissingGoogleCloud: React.FC = () => (
  <Alert message={MISSING_GOOGLE_CLOUD} showIcon type="error" />
);

const NewConnectorPreamble: React.FC = () => (
  <>
    <Paragraph>
      The Tailscale integration needs a Tailscale Premium or Enterprise plan,
      which has the custom device posture attributes that P0 sets.
    </Paragraph>
    <Paragraph>
      P0 grants access on your tailnets through a connector that you deploy in
      your own AWS account or Google Cloud project. Only the connector holds a
      Tailscale credential, and P0 stores no secret.
    </Paragraph>
    <Paragraph>
      One connector can serve many tailnets, so one is usually enough. Have
      ready P0&apos;s AWS IAM management or Google Cloud IAM management
      integration for the account or project the connector will run in. Where it
      runs can&apos;t change once the connector is added. See the <DocsLink />.
    </Paragraph>
  </>
);

/** A set-up item's Terraform, collapsed, for an admin who needs it again. */
const TerraformReference: React.FC<{
  blocks: { key: string; label: string; terraform: string }[];
  children?: ReactNode;
}> = ({ blocks, children }) => (
  <Flex gap={8} vertical>
    {children}
    <Collapse
      items={blocks.map(({ key, label, terraform }) => ({
        children: <CommandDisplay commands={terraform} maxRows={20} />,
        key,
        label,
      }))}
    />
  </Flex>
);

/** The federated identity Terraform for each installed connector, for an
 * admin who needs the identity's client ID before adding the tailnet. */
const FederatedIdentityTerraform: React.FC<{ context: InstallContext }> = ({
  context,
}) => {
  const connectors = Object.entries(
    context.config ? installedItems("connector", context.config) : {}
  );
  if (connectors.length === 0) return null;
  return (
    <TerraformReference
      blocks={connectors.flatMap(([connectorId, connector]) =>
        connector
          ? [
              {
                key: connectorId,
                label: `Show the Terraform for a federated identity that trusts connector ${connectorId}`,
                terraform: renderFederatedIdentityTerraform({
                  connectorId,
                  hosting: connector.hosting,
                }),
              },
            ]
          : []
      )}
    />
  );
};

const NewTailnetPreamble: React.FC<{ context: InstallContext }> = ({
  context,
}) => (
  <Flex gap={8} vertical>
    <Paragraph>
      Add each tailnet that P0 grants access on, by its Tailnet ID from
      Tailscale&apos;s Settings, General, such as T1234CNTRL. The tailnet&apos;s
      DNS name, such as tail1234.ts.net, can&apos;t be used. A tailnet names the
      connector that acts on it and its Tailscale credential, and neither can
      change once it&apos;s added. See the <DocsLink />.
    </Paragraph>
    <Paragraph>
      Your policy file gates grants on P0 postures, and P0 only sets device
      attributes: it never writes the policy file. The credential needs these
      scopes:
    </Paragraph>
    <ScopeList />
    <Paragraph>
      A federated identity, the default, trusts the connector&apos;s own cloud
      identity, so no secret is stored anywhere. Create it first, in the
      tailnet, and enter its client ID below. An OAuth client instead keeps its
      secret in your secret manager: {VAULT_OUTSIDE_CONNECTOR_CLOUD}
    </Paragraph>
    <FederatedIdentityTerraform context={context} />
  </Flex>
);

/** What to do with one configuration of the tailnet's read grant, shown
 * above it. `last` is whether "Next" follows it. */
const toGrantHeader = (block: ReadGrantBlock, { last }: { last: boolean }) => {
  const next = last ? <> Then click &quot;Next&quot;.</> : null;
  switch (block.purpose) {
    case "grant":
      return (
        <Paragraph>
          Add the following to your Terraform and apply it. It lets the
          connector read this tailnet&apos;s OAuth client secret.{next}
        </Paragraph>
      );
    case "vault-account":
      return (
        <>
          <Paragraph>
            Then add the following where you manage AWS account{" "}
            {block.accountId}, which holds the secret, and apply it. Add its
            output to the policy of the secret&apos;s KMS key.{next}
          </Paragraph>
          <Spacing mb={12}>
            <Alert
              message="This replaces the secret's resource policy. If the secret already has one, add the statement to that policy instead."
              showIcon
              type="warning"
            />
          </Spacing>
        </>
      );
    default:
      throw assertNever(block);
  }
};

/** What a set-up tailnet's page calls each configuration of its read
 * grant. */
const GRANT_REFERENCE_LABELS: Record<ReadGrantBlock["purpose"], string> = {
  grant: "Show the Terraform that lets the connector read the secret",
  "vault-account": "Show the Terraform for the secret's account",
};

/** Renders a tailnet's credential Terraform from what the item and its
 * connector store, or says why it can't be. */
const toCredentialTerraform = (
  context: InstallContext,
  tailnet: string,
  item: TailscaleTailnetConfig
): CredentialTerraform => {
  const hosting = context.config
    ? installedItem("connector", context.config, item.connector)?.hosting
    : undefined;
  if (!hosting) return { status: "no-connector" };
  const { credential } = item;
  switch (credential.type) {
    case "federated":
      return {
        hosting,
        status: "federated",
        terraform: renderFederatedIdentityTerraform({
          connectorId: item.connector,
          hosting,
        }),
      };
    case "oauth-client": {
      const blocks = renderReadGrantTerraform({
        connectorId: item.connector,
        credential,
        hosting,
        tailnet,
      });
      if (!blocks) return { hosting, status: "vault-outside-cloud" };
      return {
        blocks,
        hosting,
        status: "ready",
        vaultAccountId: blocks.find(isVaultAccountBlock)?.accountId,
      };
    }
    default:
      throw assertNever(credential);
  }
};

/** The Terraform blocks a tailnet's page shows, with their headers and
 * reference labels. */
const toTerraformBlocks = (terraform: CredentialTerraform) => {
  switch (terraform.status) {
    case "no-connector":
    case "vault-outside-cloud":
      return [];
    case "federated":
      return [
        {
          command: terraform.terraform,
          header: (
            <Paragraph>
              The federated identity, as Terraform for Tailscale&apos;s
              provider. Once it exists, click &quot;Next&quot;.
            </Paragraph>
          ),
          key: "federated-identity",
          label: "Show the federated identity's Terraform",
        },
      ];
    case "ready":
      return terraform.blocks.map((block, index) => ({
        command: block.terraform,
        header: toGrantHeader(block, {
          last: index === terraform.blocks.length - 1,
        }),
        key: block.purpose,
        label: GRANT_REFERENCE_LABELS[block.purpose],
      }));
    default:
      throw assertNever(terraform);
  }
};

const renderConnectorInstructions = (
  context: InstallContext,
  connectorId: string,
  { hosting }: TailscaleConnectorConfig
) => renderConnectorTerraform({ connectorId, context, hosting });

/**
 * A connector comes first. Its new item's form takes its ID and where it
 * runs. Its page shows the Terraform that deploys it, above the "Next" that
 * checks P0 can invoke it, and "Finish" on the next page installs it.
 *
 * Each tailnet then names a connector and its credential. Its page shows the
 * steps to set the credential up and gate grants on P0 postures, above the
 * "Next" that validates the install through the connector. Requestors then
 * ask for the tailnet's P0 postures, which P0 lists live.
 *
 * Once an item is set up, its page keeps its Terraform, collapsed.
 */
export const tailscaleInstaller: TailscaleInstaller = {
  components: {
    connector: {
      instructions: (context, id, item) => {
        const terraform = renderConnectorInstructions(context, id, item);
        return terraform === undefined
          ? { help: <MissingGoogleCloud /> }
          : {
              commands: { iac: [{ command: terraform }] },
              help: <ConnectorHelp connectorId={id} hosting={item.hosting} />,
            };
      },
      items: {
        hosting: {
          labeler: () =>
            "Where the connector runs. Its Terraform deploys it there, and P0 invokes it there.",
          options: {
            aws: {
              items: {
                accountId: {
                  labeler: () =>
                    "The AWS account the connector runs in. P0 lists the accounts where its AWS IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("aws"),
                },
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Lambda function. Its Terraform creates the function, and an IAM role of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The AWS region the connector runs in, such as us-west-2.",
                },
              },
            },
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, in 6 to 30 characters. Its Terraform creates the service, and a service account of the same name that the connector runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: {
                  labeler: () =>
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  optionProvider: newIamWriteInstallsProvider("gcloud"),
                },
              },
              prerequisiteMessages: async (context) =>
                p0InvokerEmail(context.installed)
                  ? undefined
                  : MISSING_GOOGLE_CLOUD,
            },
          },
        },
      },
      labeler: () =>
        "A name for this connector in P0, such as prod. Each tailnet you add picks its connector by this name.",
      newPreamble: () => <NewConnectorPreamble />,
      // The framework passes the configure form's draft, so this reads the
      // item P0 stored.
      preamble: (context, id) => {
        const stored = context.config?.connector?.[id];
        if (!stored || !isSetUp(stored)) return undefined;
        const terraform = renderConnectorInstructions(context, id, stored);
        return terraform === undefined ? (
          <MissingGoogleCloud />
        ) : (
          <TerraformReference
            blocks={[
              {
                key: "terraform",
                label: "Show the connector's Terraform",
                terraform,
              },
            ]}
          />
        );
      },
    },
    tailnet: {
      // The new item's form saved every field the Terraform needs, so render
      // it from what the item and its connector store.
      instructions: (context, tailnet, item) => {
        const terraform = toCredentialTerraform(context, tailnet, item);
        const blocks = toTerraformBlocks(terraform);
        return {
          commands:
            blocks.length > 0
              ? {
                  iac: blocks.map(({ command, header }) => ({
                    command,
                    header,
                  })),
                }
              : undefined,
          help: (
            <TailnetSteps item={item} tailnet={tailnet} terraform={terraform} />
          ),
        };
      },
      items: {
        connector: {
          optionProvider: listConnectors,
          prerequisiteMessages: async (context) =>
            (await listConnectors(context)).length === 0
              ? "Install a Tailscale connector first."
              : undefined,
        },
        credential: {
          labeler: () =>
            "How the connector authenticates to Tailscale. A federated identity needs no secret.",
          options: {
            "oauth-client": {
              items: {
                vault: {
                  labeler: () =>
                    "Where the OAuth client's secret is stored. It must be in the connector's cloud, and can be in another account or project.",
                  options: {
                    "aws-sm": {
                      items: {
                        install: {
                          labeler: () =>
                            "The AWS account the secret is in. P0 lists the accounts where its AWS Secrets Manager integration is installed.",
                          optionProvider: newIamWriteInstallsProvider("aws-sm"),
                        },
                        secretsRegion: {
                          labeler: () =>
                            "The AWS region the secret is in, such as us-west-2.",
                        },
                      },
                    },
                    "gcp-sm": {
                      items: {
                        install: {
                          labeler: () =>
                            "The Google Cloud project the secret is in. P0 lists the projects where its Google Secret Manager integration is installed.",
                          optionProvider: newIamWriteInstallsProvider("gcp-sm"),
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      labeler: () =>
        "The Tailnet ID from Tailscale's Settings, General, such as T1234CNTRL. The DNS name can't be used.",
      newPreamble: (context) => <NewTailnetPreamble context={context} />,
      // Like the connector's preamble, this reads the stored item, which also
      // has what the last validation found.
      preamble: (context, tailnet) => {
        const stored = context.config?.tailnet?.[tailnet];
        if (!stored || !isSetUp(stored)) return undefined;
        const terraform = toCredentialTerraform(context, tailnet, stored);
        return (
          <TerraformReference
            blocks={toTerraformBlocks(terraform).map(
              ({ command, key, label }) => ({
                key,
                label,
                terraform: command,
              })
            )}
          >
            <TailnetSteps
              item={stored}
              tailnet={tailnet}
              terraform={terraform}
            />
          </TerraformReference>
        );
      },
    },
  },
};
